import { useTranslation } from 'react-i18next';

import { Icon, IconButton, IconName, Link } from 'components';
import { ButtonV3, Stat } from 'components/ComponentV2';
import { LinkList } from 'components/ComponentV2/Lists';
import { ContentSection } from 'components/organization-page';
import Avatar from 'components/Avatar';
import { useResponsive } from 'hooks/useResponsive';
import { useCompany } from 'lib/providers/CompanyProvider';
import { parseArrayResponse, parseResponse } from 'lib/utils/parser';
import { PAGE_ROUTES } from 'lib/page-routes';
import { StatKindEnum } from 'lib/models/stat';
import { isDataAvailable } from 'utils/common.utils';

import styles from './CompanySidebar.module.scss';

const CompanyPartners = () => {
  const [tc] = useTranslation('common');
  const screens = useResponsive();
  const {
    company: { sponsored_societies },
  } = useCompany();
  const sponsoredSocieties = parseArrayResponse(sponsored_societies);
  if (sponsoredSocieties.length === 0) {
    return null;
  }

  return (
    <ContentSection title={tc('partners')} className={styles['partners-container']}>
      <div className={styles.partners}>
        {sponsoredSocieties.map((society) => (
          <div key={society.id} className={styles.partner}>
            <Avatar icon={society.icon_url} name={society.name} size={screens.sm ? 'medium' : 'xsmall'} />
            {screens.sm ? (
              <p className={styles.name}>{society.name}</p>
            ) : (
              <Link href={`${PAGE_ROUTES.SOCIETIES}/${society.slug}`} target="_blank" className={styles.link}>
                {society.name}
              </Link>
            )}
          </div>
        ))}
      </div>
    </ContentSection>
  );
};

const MiniStat = ({ value, label, icon }: { value: string; label: string; icon: IconName }) => {
  return (
    <div className={styles['mini-stat']}>
      <Icon iconName={icon} className={styles.icon} size="medium" />
      <div className={styles.text}>
        <span className={styles.value}>{value}</span>
        <span className={styles.label}>{label}</span>
      </div>
    </div>
  );
};

export const CompanySidebar = () => {
  const { t } = useTranslation('company');
  const screens = useResponsive();
  const { company, socialMediaLinks } = useCompany();
  const {
    industry: industryResponse,
    employees_count,
    founded_year,
    career_page_link,
    custom_links,
    achievement_stats: achievementStatsResponse,
    goal_stats: goalStatsResponse,
    insights: insightsResponse,
  } = company;
  const industry = parseResponse(industryResponse);
  const achievementStats = parseArrayResponse(achievementStatsResponse);
  const goalStats = parseArrayResponse(goalStatsResponse);
  const insights = parseArrayResponse(insightsResponse);
  const customLinks = parseArrayResponse(custom_links);
  const totalTextLinks = customLinks.length + (career_page_link ? 1 : 0);

  const showLinks = screens.sm ? totalTextLinks > 0 : totalTextLinks > 0 || socialMediaLinks.length > 0;
  const showAbout = isDataAvailable(industry?.name) || employees_count || founded_year;

  return (
    <article className={styles.sidebar}>
      <section className={styles.container}>
        {showAbout && (
          <div className={styles.about}>
            {isDataAvailable(industry?.name) && <Stat value={industry.name} label={t('sidebar.industry')} className={styles.box} />}
            {employees_count && <Stat value={employees_count} label={t('sidebar.employees')} className={styles.box} />}
            {founded_year && <Stat value={founded_year} label={t('sidebar.founded-year')} className={styles.box} />}
          </div>
        )}
        {showLinks && (
          <ContentSection title={t('sidebar.links')} className={styles['links-container']}>
            <div className={styles.links}>
              {career_page_link && (
                <a href={career_page_link} target="_blank" rel="noreferrer" className={styles.link}>
                  {screens.sm ? (
                    <ButtonV3 color="secondary" isFullWidth>
                      {t('sidebar.careers-page')}
                    </ButtonV3>
                  ) : (
                    t('sidebar.careers-page')
                  )}
                </a>
              )}
              {customLinks.length > 0 &&
                customLinks.map(({ id, title, link }) =>
                  screens.sm ? (
                    <Link key={id} href={link} target="_blank" className={styles.link}>
                      <ButtonV3 color="secondary" isFullWidth>
                        {title}
                      </ButtonV3>
                    </Link>
                  ) : (
                    <Link key={id} href={link} target="_blank" className={styles.link}>
                      {title}
                    </Link>
                  )
                )}
              {socialMediaLinks.length > 0 && (
                <div className={styles.socials}>
                  {socialMediaLinks.map(({ icon, url }) => (
                    <a href={url} key={icon} rel="noreferrer" target="_blank">
                      <IconButton iconName={icon as IconName} className={styles.icon} size="large" />
                    </a>
                  ))}
                </div>
              )}
            </div>
          </ContentSection>
        )}
        {achievementStats.length > 0 && (
          <ContentSection title={t('sidebar.achievements')} className={styles.achievements}>
            <div className={styles.stats}>
              {achievementStats.map(({ id, title, value }) => (
                <MiniStat key={id} value={value} label={title} icon="icon_achievement" />
              ))}
            </div>
          </ContentSection>
        )}
        {goalStats.length > 0 && (
          <ContentSection title={t('sidebar.goals')} className={styles.goals}>
            <div className={styles.stats}>
              {goalStats.map(({ id, title, value, kind }) => (
                <MiniStat key={id} value={value} label={title} icon={kind === StatKindEnum.NET_ZERO ? 'icon_net-zero' : 'icon_goal'} />
              ))}
            </div>
          </ContentSection>
        )}
      </section>
      <CompanyPartners />
      <div className={styles['news-list-container']} id="news">
        <LinkList title={t('navigation.news')} items={insights} size="large" />
      </div>
    </article>
  );
};

CompanySidebar.displayName = 'CompanySidebar';
